// The user manual is static content generated by scripts/import-help-docs.mjs
// into public/help-docs, so it is fetched from the app's own origin rather
// than the API. Both the manifest and topics are cached for the session.

const BASE = `${process.env.PUBLIC_URL || ""}/help-docs`;

let manifestPromise = null;
const topicCache = new Map();

// A missing file comes back as index.html with a 200 (SPA fallback), so a
// failed JSON parse is treated the same as a 404.
const fetchJson = async (url) => {
  const res = await fetch(url, { credentials: "same-origin" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  try {
    return await res.json();
  } catch {
    throw new Error("Not found");
  }
};

export const loadHelpManifest = () => {
  if (!manifestPromise) {
    manifestPromise = fetchJson(`${BASE}/manifest.json`).catch((err) => {
      manifestPromise = null;
      throw err;
    });
  }
  return manifestPromise;
};

export const loadHelpTopic = async (topicId) => {
  if (topicCache.has(topicId)) return topicCache.get(topicId);
  const data = await fetchJson(
    `${BASE}/topics/${encodeURIComponent(topicId)}.json`,
  );
  const html = typeof data?.html === "string" ? data.html : "";
  topicCache.set(topicId, html);
  return html;
};

const normalize = (text) => String(text || "").toLowerCase();

// Every term must appear in the title or body; title hits rank first.
export const searchHelpTopics = (manifest, query, limit = 50) => {
  const terms = normalize(query).split(/\s+/).filter(Boolean);
  if (!manifest || !terms.length) return [];

  const results = [];
  manifest.order.forEach((id, index) => {
    const topic = manifest.topics[id];
    const title = normalize(topic.title);
    const text = normalize(topic.text);
    let score = 0;
    for (const term of terms) {
      const inTitle = title.includes(term);
      if (!inTitle && !text.includes(term)) return;
      if (inTitle) score += 10;
      if (title.startsWith(term)) score += 5;
      if (text.includes(term)) score += 1;
    }
    results.push({ id, score, index });
  });

  return results
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .slice(0, limit)
    .map(({ id }) => id);
};

// A ~200 character window of body text around the first matching term.
export const helpSnippet = (text, query, radius = 90) => {
  const body = String(text || "");
  if (!body) return "";
  const lower = body.toLowerCase();
  const terms = normalize(query).split(/\s+/).filter(Boolean);
  const hits = terms.map((t) => lower.indexOf(t)).filter((i) => i >= 0);
  const at = hits.length ? Math.min(...hits) : 0;
  const start = Math.max(0, at - radius);
  const end = Math.min(body.length, at + radius * 1.4);
  return (
    (start > 0 ? "…" : "") +
    body.slice(start, end).trim() +
    (end < body.length ? "…" : "")
  );
};
